// ev/frontend/src/features/sightings/PlateSearch.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * PlateSearch
 *  - Mirrors prod Stats behavior (Search + Clear, "=ALIAS" for exact).
 *  - Mobile-friendly (iOS): composition-safe input, correct enterKeyHint,
 *    and suggestion chips use onMouseDown/onTouchStart to avoid blur-eating taps.
 *
 * Props:
 *  - value: string | undefined
 *  - onApply: ({ plate, exact }) => void
 *  - onClear: () => void
 *  - findCandidates?: async (query) => string[]
 */
export default function PlateSearch({ value, onApply, onClear, findCandidates }) {
  const [input, setInput] = useState(() => (typeof value === "string" ? value : ""));
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const inputRef = useRef(null);

  // keep input in sync with parent (e.g., URL/back/forward)
  useEffect(() => {
    setInput(typeof value === "string" ? value : "");
  }, [value]);

  const disabled = loading;

  const doClear = () => {
    setMatches([]);
    setInput("");
    onClear?.();
    // leave focus in the field for quick re-typing
    requestAnimationFrame(() => inputRef.current?.focus?.());
  };

  const doSearch = async (e) => {
    e?.preventDefault?.();

    // Avoid firing while IME composition is in progress (important on iOS)
    if (isComposing) return;

    const raw = String(input || "").trim();

    // empty => clear
    if (!raw) return doClear();

    // "=ALIAS" is exact (no suggestions)
    if (raw.startsWith("=")) {
      const alias = raw.slice(1).trim();
      setMatches([]);
      onApply?.({ plate: alias, exact: true });
      return;
    }

    // Try to resolve partials via resolver (if provided)
    if (typeof findCandidates === "function") {
      try {
        setLoading(true);
        const list = (await findCandidates(raw)) || [];
        const unique = Array.from(new Set(list.map(String))).filter(Boolean);

        if (unique.length === 0) {
          // No candidates -> treat as partial wildcard
          setMatches([]);
          onApply?.({ plate: raw, exact: false });
          return;
        }
        if (unique.length === 1) {
          // Single candidate -> go exact
          setMatches([]);
          onApply?.({ plate: unique[0], exact: true });
          return;
        }

        // >1 candidates -> show chips (sorted, max 24)
        const sorted = unique.sort((a, b) => a.localeCompare(b)).slice(0, 24);
        setMatches(sorted);
        return;
      } finally {
        setLoading(false);
      }
    }

    // Fallback (no resolver): treat as partial
    setMatches([]);
    onApply?.({ plate: raw, exact: false });
  };

  const pickExact = (p) => {
    setMatches([]);
    onApply?.({ plate: p, exact: true });
  };

  // styling consistent with current dark UI
  const ui = useMemo(
    () => ({
      row: { display: "flex", alignItems: "center", gap: 12, rowGap: 12, flexWrap: "wrap" },
      input: {
        height: 32,
        background: "#0d1420",
        border: "1px solid #1f2a39",
        color: "#dbe7ff",
        borderRadius: 6,
        padding: "0 10px",
        width: 280,
        cursor: "text",
        WebkitUserSelect: "text",
        WebkitTouchCallout: "default",
        touchAction: "manipulation",
      },
      btn: {
        height: 32,
        padding: "0 12px",
        borderRadius: 6,
        background: "#14304f",
        border: "1px solid #2a4d77",
        color: "#dbe7ff",
        cursor: "pointer",
        opacity: disabled ? 0.7 : 1,
      },
      matchesWrap: { marginTop: 8, display: matches.length ? "block" : "none" },
      hint: { fontSize: 12, opacity: 0.85, marginBottom: 6 },
      chipRow: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
      chip: {
        display: "inline-flex",
        alignItems: "center",
        height: 28,
        padding: "0 10px",
        borderRadius: 999,
        background: "#0f2239",
        border: "1px solid #2a4d77",
        color: "#cfe0ff",
        fontSize: 12,
        cursor: "pointer",
        userSelect: "none",
      },
    }),
    [disabled, matches.length]
  );

  return (
    <>
      {/* Input row */}
      <form style={ui.row} onSubmit={doSearch}>
        <label style={{ opacity: 0.8 }}>Search for Plate</label>
        <input
          ref={inputRef}
          type="text"
          inputMode="text"
          autoCorrect="off"
          autoCapitalize="none"
          autoComplete="off"
          spellCheck={false}
          enterKeyHint="search"
          placeholder="e.g., PC503081, partial like DOM, or =ALIAS"
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            setMatches([]); // hide old matches on edit
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !isComposing) {
              e.preventDefault();
              doSearch();
            }
          }}
          onCompositionStart={() => setIsComposing(true)}
          onCompositionEnd={() => setIsComposing(false)}
          style={ui.input}
          disabled={disabled}
        />
        <button type="submit" style={ui.btn} disabled={disabled}>
          {loading ? "Searching…" : "Search"}
        </button>
        <button type="button" style={ui.btn} onClick={doClear} disabled={disabled}>
          Clear
        </button>
      </form>

      {/* Matches row (shown only when >1 candidates) */}
      <div style={ui.matchesWrap}>
        <div style={ui.hint}>
          Multiple plates match <em>“{input}”</em>. Pick the exact plate to load precise results.
        </div>
        <div style={ui.chipRow}>
          {matches.map((p) => (
            <button
              key={p}
              type="button"
              style={ui.chip}
              // Use mousedown/touchstart so taps register even if the input blurs first on mobile
              onMouseDown={() => pickExact(p)}
              onTouchStart={() => pickExact(p)}
              title={`Use exact plate ${p}`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
